import React, { createContext, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
// Side effect: i18next is initialized before the provider reads it.
import './i18n'
import { sdk } from './sdk'

export type SupportedLocale =
  | 'en-US' | 'pt-BR' | 'es-ES' | 'es-MX' | 'es-AR' | 'pt-PT' | 'it-IT' | 'de-DE' | 'de-AT' | 'de-CH'
  | 'fr-FR' | 'fr-BE' | 'fr-CA' | 'fr-CH'

interface LocaleContextType {
  currentLocale: SupportedLocale
  changeLanguage: (locale: SupportedLocale) => Promise<void>
  t: (key: string, options?: any) => string
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined)

const normalizeLocale = (locale: string): SupportedLocale => {
  const supportedLocales: SupportedLocale[] = [
    'en-US', 'pt-BR', 'pt-PT', 'es-ES', 'es-MX', 'es-AR', 'it-IT', 'de-DE', 'de-AT', 'de-CH',
    'fr-FR', 'fr-BE', 'fr-CA', 'fr-CH'
  ]

  if (supportedLocales.includes(locale as SupportedLocale)) {
    return locale as SupportedLocale
  }

  return 'en-US'
}

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t, i18n: i18nInstance, ready } = useTranslation()
  const [currentLocale, setCurrentLocale] = useState<SupportedLocale>('en-US')
  const [isInitialized, setIsInitialized] = useState(false)

  // Initialize locale - sync with actual i18n language
  useEffect(() => {
    if (ready && !isInitialized) {
      const actualLanguage = i18nInstance.language || i18nInstance.options.lng || 'en-US'
      const locale = normalizeLocale(actualLanguage)
      console.log('LocaleProvider: Initializing with locale:', locale, '(from i18n:', actualLanguage, ')')
      setCurrentLocale(locale)
      sdk.setLocale(locale)
      setIsInitialized(true)
    }
  }, [ready, isInitialized, i18nInstance])

  // Keep currentLocale in sync with i18n language changes
  useEffect(() => {
    if (ready) {
      const actualLanguage = i18nInstance.language || 'en-US'
      const locale = normalizeLocale(actualLanguage)
      if (locale !== currentLocale) {
        console.log('LocaleProvider: Syncing locale from', currentLocale, 'to', locale)
        setCurrentLocale(locale)
      }
    }
  }, [ready, i18nInstance.language, currentLocale])

  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      const locale = normalizeLocale(lng)
      console.log('LocaleProvider: Language changed to:', locale)
      setCurrentLocale(locale)
      sdk.setLocale(locale)
    }

    i18nInstance.on('languageChanged', handleLanguageChange)

    return () => {
      i18nInstance.off('languageChanged', handleLanguageChange)
    }
  }, [i18nInstance])

  const changeLanguage = async (locale: SupportedLocale) => {
    console.log('LocaleProvider: Changing language to:', locale)
    try {
      await i18nInstance.changeLanguage(locale)
      setCurrentLocale(locale)

      // Save to localStorage explicitly
      localStorage.setItem('i18nextLng', locale)

      // Sync locale with SDK for API requests
      sdk.setLocale(locale)

      console.log('LocaleProvider: Language changed successfully to:', locale)
      console.log('LocaleProvider: Current i18n language:', i18nInstance.language)
      console.log('LocaleProvider: localStorage value:', localStorage.getItem('i18nextLng'))
    } catch (error) {
      console.error('LocaleProvider: Error changing language:', error)
      throw error
    }
  }

  if (!ready) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <LocaleContext.Provider value={{ currentLocale, changeLanguage, t }}>
      {children}
    </LocaleContext.Provider>
  )
}

export const useLocale = () => {
  const context = useContext(LocaleContext)
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider')
  }
  return context
}
